import React, { useEffect, useState } from "react";
import { redirectToSubdomain, getCurrentSubdomain, getRootDomainUrl, isRootHost } from "../../utils/tenant";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { useLocation } from "react-router-dom";
import {
  APP_SUBDOMAIN_PUBLIC_PATHS,
  AUTH_PATHS,
  DEFAULT_PATHS,
  isPublicPath,
  ROOT_DOMAIN_PATHS,
} from "../../routing/paths.ts";
import { postAuthExchange } from "../../api/oauth/requests.ts";

const AUTH_BOOTSTRAP_EVENT = "skeptics-auth-bootstrap-ready";
const AUTH_BOOTSTRAP_FLAG = "__SKEPTICS_AUTH_BOOTSTRAP_IN_PROGRESS__";
let handoffExchangeInFlight: Promise<void> | null = null;

const AUTH_KEYS = [
  "username",
  "userId",
  "organization_slug",
  "organization_id",
  "is_platform_admin",
  "auth_user",
];

const TENANT_KEYS = ["organization_slug","organization_id"];
const ADMIN_KEYS = ["is_platform_admin"];

function clearAllAuth() {
  AUTH_KEYS.forEach((k) => localStorage.removeItem(k));
}

function clearTenantData() {
  TENANT_KEYS.forEach((k) => localStorage.removeItem(k));
}

function clearAdminData() {
  ADMIN_KEYS.forEach((k) => localStorage.removeItem(k));
}

/**
 * Consumes auth params from URL (hash or query) and stores in localStorage.
 * Used when redirecting from app to admin/tenant - localStorage is not shared across subdomains.
 */
async function consumeAuthFromUrl() {
  const hash = window.location.hash?.slice(1) || "";
  const query = window.location.search?.slice(1) || "";
  const params = new URLSearchParams(hash || query);

  const handoffToken = params.get("handoff_token");
  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");
  if (!handoffToken && !accessToken) return null;

  const isPlatformAdmin = params.get("is_platform_admin") === "true";
  if (isPlatformAdmin) {
    clearTenantData();
  } else {
    clearAdminData();
  }

  const username = params.get("username") || "";
  localStorage.setItem("username", username);

  const userIdFromUrl = params.get("user_id");
  if (userIdFromUrl) {
    localStorage.setItem("userId", userIdFromUrl);
  }

  const organizationSlug = params.get("organization_slug");
  const organizationId = String(params.get("organization_id") ?? "");
  if (organizationSlug) {
    localStorage.setItem("organization_slug", organizationSlug ?? "");
  }

  if (organizationId) {
    localStorage.setItem("organization_id", organizationId);
  }

  const isPlatformAdminParam = params.get("is_platform_admin");
  if (isPlatformAdminParam !== null && isPlatformAdminParam !== undefined) {
    localStorage.setItem("is_platform_admin", isPlatformAdminParam);
  }

  // Store a lightweight user object for auth checks on this subdomain.
  try {
    const user = {
      username,
      is_platform_admin: isPlatformAdmin,
      organization_slug: organizationSlug || "",
      organization_id: organizationId || "",
      ...(userIdFromUrl ? { id: userIdFromUrl } : {}),
    };
    localStorage.setItem("auth_user", JSON.stringify(user));
  } catch (e) {
    // ignore storage errors
  }
  if ((process.env.NODE_ENV === 'development' || process.env.REACT_APP_USE_TOKEN_AUTH === 'true') && accessToken) {
      localStorage.setItem("access_token", accessToken || '');
      localStorage.setItem("refresh_token", refreshToken || '');
  }

  if (handoffToken) {
    if (!handoffExchangeInFlight) {
      handoffExchangeInFlight = postAuthExchange({
        handoff_token: handoffToken,
        organization_id: organizationId || undefined,
        organization_slug: organizationSlug || undefined,
      })
        .then((exchangeData) => {
          if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_USE_TOKEN_AUTH === 'true') {
            if (exchangeData?.access_token) {
              localStorage.setItem("access_token", exchangeData.access_token);
            }
            if (exchangeData?.refresh_token) {
              localStorage.setItem("refresh_token", exchangeData.refresh_token);
            }
          }
          // Merge org-scoped fields from the exchange response user into auth_user.
          // SubdomainAuthGuard builds auth_user from URL params and doesn't include
          // fields like org_role or has_own_ai_credentials that the API enriches.
          // Patching here ensures sidebar visibility rules (e.g. AI Activity) work
          // correctly without requiring a separate /me API call.
          if (exchangeData?.user) {
            try {
              const stored = JSON.parse(localStorage.getItem("auth_user") || "{}");
              const enriched = {
                ...stored,
                org_role: exchangeData.user.org_role ?? stored.org_role,
                has_own_ai_credentials: exchangeData.user.has_own_ai_credentials ?? stored.has_own_ai_credentials ?? false,
              };
              localStorage.setItem("auth_user", JSON.stringify(enriched));
            } catch {
              // ignore storage errors
            }
          }
        })
        .finally(() => {
          handoffExchangeInFlight = null;
        });
    }
    await handoffExchangeInFlight;
  }
  // Strip auth params from URL (hash/query) after storing them in localStorage.
  // This uses history.replaceState so it does NOT trigger a full reload.
  const cleanUrl = window.location.pathname + window.location.search;
  window.history.replaceState(null, "", cleanUrl);

  return handoffToken || accessToken;
}

function SubdomainAuthGuard({ children }: {children: React.ReactNode}) {
  const [checking, setChecking] = useState(true);
  const { setTheme } = useTheme();
  const location = useLocation();
  const path = location.pathname

  useEffect(() => {
    let cancelled = false;
    const runGuard = async () => {
    (window as any)[AUTH_BOOTSTRAP_FLAG] = true;
    const hostname = window.location.hostname;
    const search = window.location.search || "";
    const subdomain = getCurrentSubdomain();
    const rootHost = isRootHost(hostname);

    // 0. Root = effective domain with no subdomain.
    // Production: REACT_APP_BASE_DOMAIN (e.g. skeptics.ai).
    // Non-production: REACT_APP_DOMAIN_ENV.REACT_APP_BASE_DOMAIN (e.g. qa.skeptics.ai).

    if (rootHost) {
      const pathNormalized = path.replace(/\/$/, "") || "/";
      // Public paths that live on app subdomain: redirect from root host to app.
      if (APP_SUBDOMAIN_PUBLIC_PATHS.includes(pathNormalized)) {
        clearAllAuth();
        redirectToSubdomain("app", pathNormalized + search);
        return;
      }
      // Root host always uses default branding.
      setTheme({ primaryColor: null, logoUrl: null });
      // Otherwise allow full access to the core site without auth redirects.
      if (!cancelled) {
        setChecking(false);
      }
      return;
    }

    // 1. Logout or force signin: when landing on app with ?logout=1 or ?force_signin=1, clear auth first (app has its own localStorage)
    const searchParams = new URLSearchParams(search);
    if (searchParams.get("logout") === "1" || searchParams.get("force_signin") === "1") {
      clearAllAuth();
      window.history.replaceState(null, "", path);
    }

    // 2. Consume auth from URL (passed when redirecting from app signin to admin/tenant)
    try {
      await consumeAuthFromUrl();
    } catch (err) {
      clearAllAuth();
      redirectToSubdomain("app", `${AUTH_PATHS.SIGNIN}?force_signin=1`);
      return;
    } finally {
      (window as any)[AUTH_BOOTSTRAP_FLAG] = false;
      window.dispatchEvent(new Event(AUTH_BOOTSTRAP_EVENT));
    }

    // 3. Check for stored auth (slug + user) before loading organization settings.
    // ThemeProvider is responsible for fetching org settings from the backend and
    // caching theme in localStorage; here we only clear theme for public routes.
    const isPublicRoute = isPublicPath(path);

    // Keep public routes unbranded; tenant logo/theme is for authenticated app pages only.
    if (isPublicRoute) {
      setTheme({ primaryColor: null, logoUrl: null });
    }

    const isAppSubdomain = subdomain === "app";
    if (isAppSubdomain && !isPublicPath(path)) {
      clearAllAuth();
      redirectToSubdomain("app", "/signin");
      return;
    }
    
    const isAdminSubdomain = subdomain === "admin";

    // 4. App subdomain routing rules
    if (isAppSubdomain) {
      const pathNormalized = path.replace(/\/$/, "") || "/";

      // Paths in ROOT_DOMAIN_PATHS (/, /contact, /home) must be on root domain, not app.<domain>
      if (ROOT_DOMAIN_PATHS.includes(pathNormalized)) {
        window.location.replace(getRootDomainUrl(pathNormalized + search));
        return;
      }

      // Allow public (unauthenticated) routes on app subdomain
      if (isPublicPath(path)) {
        if (!cancelled) {
          setChecking(false);
        }
        return;
      }

      // If we have an org slug and the user is a tenant user, they should never
      // stay on app.* except for /signin. Send them to their tenant subdomain.
      const orgSlug = localStorage.getItem("organization_slug") || localStorage.getItem("tenantSlug");
      const isPlatformAdmin = localStorage.getItem("is_platform_admin") === "true";

      if (orgSlug && !isPlatformAdmin) {
        // Send tenant users to their own subdomain dashboard
        redirectToSubdomain(orgSlug, DEFAULT_PATHS.TENANT_LANDING);
        return;
      }
      if(isPlatformAdmin && !path.includes("admin")){
        clearAllAuth();
        redirectToSubdomain("app", AUTH_PATHS.SIGNIN);
        return;
      }
      if (isPlatformAdmin) {
        // Platform admins should be on admin.* for app content, not app.*
        redirectToSubdomain("admin", DEFAULT_PATHS.ADMIN_LANDING);
        return;
      }

      // Unknown state on app subdomain: do not force a signin redirect here.
      // Let the generic "no token + subdomain + !signin" rule handle redirects.
      if (!cancelled) {
        setChecking(false);
      }
      return;
    }

    const storedUserRaw = localStorage.getItem("auth_user");
    const hasUser = !!storedUserRaw;

    // 5. Single rule for redirecting to signin:
    // - no stored user
    // - we are NOT on the env root host (so there *is* a subdomain)
    // - the current path is not a public (unauthenticated) route
    const hasSubdomainHost = !rootHost;
    if (!hasUser && hasSubdomainHost && !isPublicPath(path)) {
      clearAllAuth()
      redirectToSubdomain("app", AUTH_PATHS.SIGNIN);
      return;
    }

    // 6. Admin subdomain
    if (isAdminSubdomain && hasUser) {
      const orgSlug = localStorage.getItem("organization_slug");
      const isPlatformAdmin = localStorage.getItem("is_platform_admin") === "true";

      // If we have tenant data in localStorage (same-origin), redirect tenant users
      // away from admin back to their organization subdomain instead of app.
      if (orgSlug && !isPlatformAdmin) {
        redirectToSubdomain(orgSlug, DEFAULT_PATHS.TENANT_LANDING);
        return;
      }

      // If we somehow have no orgSlug but also are not marked as platform admin,
      // treat this as an invalid admin session and send to app signin.
      if (!isPlatformAdmin) {
        clearAllAuth();
        redirectToSubdomain("app", `${AUTH_PATHS.SIGNIN}?force_signin=1`);
        return;
      }

      // Happy path for freshly signed-in platform admins:
      // trust the local flag and avoid an extra network round-trip that causes a visible redirect/flicker.
      if (isPlatformAdmin) {
        if (!cancelled) {
          setChecking(false);
        }
        return;
      }
    }

    // 7. Tenant subdomain: as long as we have a token and we're not on app/admin,
    // trust the URL and allow access. We no longer hard-redirect back to app
    // based on slug mismatches to avoid double-login loops.

    if (!cancelled) {
      setChecking(false);
    }
      return;
    };
    runGuard();
    return () => {
      cancelled = true;
    };
  }, [path, setTheme]);

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Checking authentication...</div>
      </div>
    );
  }

  return children;
}

export default SubdomainAuthGuard;
export { clearAllAuth, clearTenantData, clearAdminData };
